import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTenantStore } from '@/store/tenantStore'
import { Button3D } from '@/components/learner/ui/Button3D'
import KakaoPostcode from '@/components/common/KakaoPostcode'
import BirthDatePicker from '@/components/common/BirthDatePicker'
import { getApiErrorMessage } from '@/utils/apiError'
import { validateLearnerHandle } from '@/utils/learnerEmail'
import { deriveGradeKey } from '@/utils/gradeDerivation'
import {
  requestSelfRegisterSms,
  verifySelfRegisterSms,
  checkSelfRegisterHandle,
  submitSelfRegister,
  TERMS_VERSION,
  type SelfRegisterChild,
} from '@/api/selfRegister'

const ConsentDetailModal = lazy(() =>
  import('@/components/auth/ConsentDetailModal').then((module) => ({
    default: module.ConsentDetailModal,
  })),
)

type Step = 'sms' | 'verify' | 'form' | 'done'
type HandleStatus = 'idle' | 'checking' | 'ok' | 'taken' | 'invalid'

export default function SelfRegisterPage() {
  const { t } = useTranslation('learner')
  const centerName = useTenantStore((s) => s.tenantInfo?.displayName ?? s.tenantInfo?.name) ?? ''

  const [step, setStep] = useState<Step>('sms')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // SMS
  const [guardianName, setGuardianName] = useState('')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [resendIn, setResendIn] = useState(0)
  const [sessionToken, setSessionToken] = useState('')
  const [children, setChildren] = useState<SelfRegisterChild[]>([])

  // student form
  const [handle, setHandle] = useState('')
  const [handleStatus, setHandleStatus] = useState<HandleStatus>('idle')
  const [studentName, setStudentName] = useState('')
  const [dob, setDob] = useState('')
  const [gender, setGender] = useState('')
  const [school, setSchool] = useState('')
  // guardian address
  const [postalCode, setPostalCode] = useState('')
  const [addressBase, setAddressBase] = useState('')
  const [addressDetail, setAddressDetail] = useState('')
  // consent
  const [termsAgreed, setTermsAgreed] = useState(false)
  const [marketingAgreed, setMarketingAgreed] = useState(false)
  const [detailKind, setDetailKind] = useState<'terms' | 'marketing' | null>(null)
  const [resultHandle, setResultHandle] = useState('')
  const [alreadyRegistered, setAlreadyRegistered] = useState(false)

  const allAgreed = termsAgreed && marketingAgreed
  const gradeKey = deriveGradeKey(dob)
  const gradeLabel = (key: string) =>
    t(`dashboard.gradeLevel.${key}`, { ns: 'admin', defaultValue: key })

  useEffect(() => {
    if (resendIn <= 0) return
    const id = setTimeout(() => setResendIn((v) => v - 1), 1000)
    return () => clearTimeout(id)
  }, [resendIn])

  // 실시간 중복확인 — sessionToken 있을 때만(게이트). debounce 400ms.
  const debRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current)
    const v = handle.trim()
    if (!sessionToken || v.length === 0) { setHandleStatus('idle'); return }
    if (validateLearnerHandle(v) !== 'ok') { setHandleStatus('invalid'); return }
    setHandleStatus('checking')
    debRef.current = setTimeout(async () => {
      try {
        const r = await checkSelfRegisterHandle(sessionToken, v)
        setHandleStatus(r.available ? 'ok' : 'taken')
      } catch {
        setHandleStatus('idle')
      }
    }, 400)
    return () => { if (debRef.current) clearTimeout(debRef.current) }
  }, [handle, sessionToken])

  const wrap = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (e) {
      setError(getApiErrorMessage(e, t))
    } finally {
      setBusy(false)
    }
  }

  const onSendCode = () => wrap(async () => {
    await requestSelfRegisterSms(guardianName, phone)
    setStep('verify')
    setResendIn(60)
  })

  const onVerify = () => wrap(async () => {
    const r = await verifySelfRegisterSms(phone, code)
    setSessionToken(r.sessionToken)
    setChildren(r.children)
    setStep('form')
  })

  const onSubmit = () => wrap(async () => {
    const r = await submitSelfRegister(
      sessionToken,
      {
        handle: handle.trim(),
        fullName: studentName.trim(),
        dateOfBirth: dob,
        gender: gender || undefined,
        school: school || undefined,
      },
      {
        name: guardianName.trim(),
        phone,
        postalCode: postalCode || undefined,
        addressBase: addressBase || undefined,
        addressDetail: addressDetail || undefined,
      },
      { termsAgreed, marketingAgreed, termsVersion: TERMS_VERSION },
    )
    setResultHandle(r.handle)
    setAlreadyRegistered(r.alreadyRegistered)
    setStep('done')
  })

  const canSubmit =
    !busy && termsAgreed && handle.trim().length > 0 && handleStatus !== 'taken' &&
    handleStatus !== 'invalid' && studentName.trim().length > 0 && dob.length > 0

  return (
    <div className="min-h-dvh bg-lc-gray-50 flex flex-col items-center px-3 py-5 sm:px-4 sm:py-10">
      <div className="w-full max-w-[480px] bg-white rounded-lc-xl shadow-lc-soft p-5 sm:p-7">
        <h1 className="text-lc-headline-2 text-lc-gray-900 mb-1">{t('auth.selfRegister.title')}</h1>
        {centerName && <p className="text-lc-body-3 text-lc-gray-600 mb-6">{centerName}</p>}

        {step === 'sms' && (
          <div className="space-y-4">
            <Field label={t('auth.selfRegister.guardianName')} value={guardianName} onChange={setGuardianName} />
            <Field
              label={t('auth.selfRegister.guardianPhone')}
              value={phone}
              onChange={setPhone}
              inputMode="numeric"
              placeholder="01012345678"
            />
            <Button3D
              type="button"
              variant="orange"
              size="L"
              disabled={busy || !guardianName || !phone}
              onClick={onSendCode}
              className="w-full"
            >
              {t('auth.selfRegister.sendCode')}
            </Button3D>
          </div>
        )}

        {step === 'verify' && (
          <div className="space-y-4">
            <Field
              label={t('auth.selfRegister.code')}
              value={code}
              onChange={setCode}
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
            />
            <Button3D
              type="button"
              variant="orange"
              size="L"
              disabled={busy || !code}
              onClick={onVerify}
              className="w-full"
            >
              {t('auth.selfRegister.verify')}
            </Button3D>
            <button
              type="button"
              disabled={busy || resendIn > 0}
              onClick={onSendCode}
              className="w-full text-center text-lc-body-3 text-lc-lightblue-900 hover:underline disabled:opacity-50"
            >
              {resendIn > 0
                ? t('auth.selfRegister.resendIn', { value: resendIn })
                : t('auth.selfRegister.resend')}
            </button>
          </div>
        )}

        {step === 'form' && (
          <div className="space-y-5">
            {children.length > 0 && (
              <div className="rounded-lc-md bg-lc-gray-100 p-4">
                <p className="text-lc-body-3 font-semibold text-lc-gray-800 mb-2">
                  {t('auth.selfRegister.childrenTitle')}
                </p>
                <ul className="space-y-1">
                  {children.map((c) => (
                    <li key={c.handle} className="text-lc-body-3 text-lc-gray-700">
                      {c.studentName} · {c.handle}
                      {c.gradeLabel ? ` · ${gradeLabel(c.gradeLabel)}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* 원생 정보 */}
            <div>
              <Field label={t('auth.selfRegister.handle')} value={handle} onChange={setHandle} />
              {handleStatus === 'ok' && <Hint ok>{t('auth.selfRegister.handleOk')}</Hint>}
              {handleStatus === 'taken' && <Hint>{t('auth.selfRegister.handleTaken')}</Hint>}
              {handleStatus === 'invalid' && <Hint>{t('auth.selfRegister.handleInvalid')}</Hint>}
            </div>
            <Field label={t('auth.selfRegister.studentName')} value={studentName} onChange={setStudentName} />
            <div>
              <span className="block text-[14px] font-semibold text-lc-gray-800 mb-1.5">
                {t('auth.selfRegister.dob')}
              </span>
              <BirthDatePicker
                aria-label={t('auth.selfRegister.dob')}
                value={dob}
                onChange={setDob}
                inputClassName="w-full h-12 px-4 rounded-lc-md border border-lc-gray-200 bg-white text-lc-body-2 text-lc-gray-700 placeholder-lc-gray-500 outline-none focus:border-[1.5px] focus:border-lc-lightblue-900"
              />
              {gradeKey && <Hint ok>{t('auth.selfRegister.derivedGrade', { grade: gradeLabel(gradeKey) })}</Hint>}
            </div>
            <div>
              <span className="block text-[14px] font-semibold text-lc-gray-800 mb-1.5">
                {t('auth.selfRegister.gender')}
              </span>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className="w-full h-12 px-4 rounded-lc-md border border-lc-gray-200 bg-white"
              >
                <option value="">{t('auth.selfRegister.genderNone')}</option>
                <option value="male">{t('auth.selfRegister.genderMale')}</option>
                <option value="female">{t('auth.selfRegister.genderFemale')}</option>
              </select>
            </div>
            <Field label={t('auth.selfRegister.school')} value={school} onChange={setSchool} />

            {/* 학부모 정보 */}
            <Field label={t('auth.selfRegister.guardianName')} value={guardianName} onChange={setGuardianName} />
            <div>
              <span className="block text-[14px] font-semibold text-lc-gray-800 mb-1.5">
                {t('auth.selfRegister.address')}
              </span>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  readOnly
                  value={addressBase}
                  placeholder={t('auth.selfRegister.addressBase')}
                  className="min-w-0 flex-1 h-12 px-4 rounded-lc-md border border-lc-gray-200 bg-lc-gray-50 text-lc-body-2 text-lc-gray-700 placeholder-lc-gray-500"
                />
                <KakaoPostcode
                  label={t('auth.selfRegister.searchAddress')}
                  className="h-12 w-full rounded-lc-md border border-lc-gray-200 bg-white px-4 text-lc-body-3 font-semibold text-lc-gray-800 shadow-lc-chip transition hover:bg-lc-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-lightblue-900 sm:w-auto sm:shrink-0"
                  onSelect={(s) => { setPostalCode(s.postalCode); setAddressBase(s.addressBase) }}
                />
              </div>
              <input
                value={addressDetail}
                onChange={(e) => setAddressDetail(e.target.value)}
                placeholder={t('auth.selfRegister.addressDetail')}
                className="mt-2 w-full h-12 px-4 rounded-lc-md border border-lc-gray-200 bg-white"
              />
            </div>

            {/* 동의 */}
            <div className="rounded-lc-md border border-lc-gray-200 bg-white p-4 space-y-3">
              <label className="flex items-center gap-3 font-semibold text-lc-gray-900">
                <input
                  type="checkbox"
                  checked={allAgreed}
                  onChange={(e) => { setTermsAgreed(e.target.checked); setMarketingAgreed(e.target.checked) }}
                  className="size-5 shrink-0 rounded border-lc-gray-300 text-lc-lightblue-900 focus:ring-lc-lightblue-900"
                />
                {t('auth.selfRegister.agreeAll')}
              </label>
              <ConsentRow
                label={t('auth.selfRegister.agreeTerms')}
                tag={t('auth.selfRegister.requiredTag')}
                checked={termsAgreed}
                onChange={setTermsAgreed}
                onDetail={() => setDetailKind('terms')}
                detailLabel={t('auth.selfRegister.viewDetail')}
              />
              <ConsentRow
                label={t('auth.selfRegister.agreeMarketing')}
                tag={t('auth.selfRegister.optionalTag')}
                checked={marketingAgreed}
                onChange={setMarketingAgreed}
                onDetail={() => setDetailKind('marketing')}
                detailLabel={t('auth.selfRegister.viewDetail')}
              />
            </div>

            <Button3D
              type="button"
              variant="orange"
              size="L"
              disabled={!canSubmit}
              onClick={onSubmit}
              className="w-full"
            >
              {t('auth.selfRegister.submit')}
            </Button3D>
          </div>
        )}

        {step === 'done' && (
          <div className="space-y-4 text-center">
            <p className="text-lc-body-2 font-semibold text-lc-gray-900">
              {alreadyRegistered ? t('auth.selfRegister.alreadyRegistered') : t('auth.selfRegister.doneTitle')}
            </p>
            {!alreadyRegistered && (
              <div className="rounded-lc-md bg-lc-gray-100 p-4 text-left text-lc-body-3 text-lc-gray-800">
                <p>{t('auth.selfRegister.doneHandle', { handle: resultHandle })}</p>
                <p>{t('auth.selfRegister.donePassword')}</p>
              </div>
            )}
          </div>
        )}

        {error && <p role="alert" className="mt-4 text-lc-body-3 text-lc-red-dark">{error}</p>}
      </div>
      {detailKind != null ? (
        <Suspense fallback={null}>
          <ConsentDetailModal open kind={detailKind} onClose={() => setDetailKind(null)} />
        </Suspense>
      ) : null}
    </div>
  )
}

interface FieldProps {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  inputMode?: 'numeric'
  maxLength?: number
  placeholder?: string
}

function Field({ label, value, onChange, type = 'text', inputMode, maxLength, placeholder }: FieldProps) {
  return (
    <div>
      <span className="block text-[14px] font-semibold text-lc-gray-800 mb-1.5">{label}</span>
      <input
        aria-label={label}
        value={value}
        type={type}
        inputMode={inputMode}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-12 px-4 rounded-lc-md border border-lc-gray-200 bg-white text-lc-body-2 text-lc-gray-700 placeholder-lc-gray-500 outline-none focus:border-[1.5px] focus:border-lc-lightblue-900"
      />
    </div>
  )
}

function Hint({ children, ok = false }: { children: React.ReactNode; ok?: boolean }) {
  return <p className={`mt-1 text-lc-body-3 ${ok ? 'text-lc-mint-900' : 'text-lc-red-dark'}`}>{children}</p>
}

interface ConsentRowProps {
  label: string
  tag: string
  checked: boolean
  onChange: (v: boolean) => void
  onDetail: () => void
  detailLabel: string
}

function ConsentRow({ label, tag, checked, onChange, onDetail, detailLabel }: ConsentRowProps) {
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3">
      <label className="contents text-lc-body-3 text-lc-gray-800">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 size-5 shrink-0 rounded border-lc-gray-300 text-lc-lightblue-900 focus:ring-lc-lightblue-900"
        />
        <span className="min-w-0 leading-6">
          <span className="font-medium text-lc-gray-600">{tag}</span>
          <span className="ml-1 text-lc-gray-800">{label}</span>
        </span>
      </label>
      <button
        type="button"
        onClick={onDetail}
        className="pt-0.5 text-lc-body-3 font-semibold text-lc-lightblue-dark underline underline-offset-2"
      >
        {detailLabel}
      </button>
    </div>
  )
}
